/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ApiStatus } from '../../../ts';
import { FeatureFlag } from '../../../ts/feature-flags';
import {
  CUSTOM_SERVICE_SEARCH_AUTH_TYPES,
  SEARCH_PROVIDER_TYPES
} from '../../../ts/skill';
import type { RootState } from '../../../ts/state';
import {
  CONFIDENCE_THRESHOLD_VALUE,
  RESPONSE_LENGTH_VALUE
} from '../../discovery/util/constants';
import { isFeatureEnabled } from '../../ia/store/features/selectors';

export const selectIsDirty = (state: RootState) => state.customService?.isDirty;

export const selectIsEditing = (state: RootState) =>
  state.customService?.isEditing;

export const selectDraftSkillId = (state: RootState) =>
  state.customService?.draft?.skill_id;

export const selectDraftSearchSkill = (state: RootState) =>
  state.customService?.draft;

export const selectDraftSuccessMessage = (state: RootState) =>
  state.customService?.draft?.search_settings?.messages?.success ?? '';

export const selectDraftNoResultsMessage = (state: RootState) =>
  state.customService?.draft?.search_settings?.messages?.no_result ?? '';

export const selectDraftConnectivityMessage = (state: RootState) =>
  state.customService?.draft?.search_settings?.messages?.error ?? '';

export const selectDraftElasticFilterValue = (state: RootState) =>
  state.customService?.draft?.search_settings?.client_side_search?.filter;

export const selectDraftCitationsTitleValue = (state: RootState) =>
  state.customService?.draft?.search_settings?.conversational_search
    ?.citations_title ?? '';

export const selectDraftMaxCitationsValue = (state: RootState) =>
  state.customService?.draft?.search_settings?.conversational_search?.citations
    ?.max_citations ?? -1;

export const selectDraftIsConversationalSearchEnabled = (state: RootState) =>
  state.customService?.draft?.search_settings?.conversational_search?.enabled ??
  false;

export const selectDraftConversationalSearchRetrievalConfidenceThreshold = (
  state: RootState
) =>
  state.customService?.draft?.search_settings?.conversational_search
    ?.search_confidence?.pre_gen_threshold ??
  CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN;

export const selectDraftConversationalSearchConfidenceThreshold = (
  state: RootState
) =>
  state.customService?.draft?.search_settings?.conversational_search
    ?.search_confidence?.threshold ?? CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN;

export const selectDraftConversationalSearchResponseConfidenceThreshold = (
  state: RootState
) =>
  state.customService?.draft?.search_settings?.conversational_search
    ?.search_confidence?.post_gen_threshold ??
  CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN;

export const selectDraftConversationalSearchMultiTurn = (state: RootState) =>
  state.customService?.draft?.search_settings?.conversational_search?.multi_turn
    ?.enabled ?? false;

export const selectDraftConversationalSearchVerbosityOption = (
  state: RootState
) =>
  state.customService?.draft?.search_settings?.conversational_search
    ?.response_length?.option ?? RESPONSE_LENGTH_VALUE.MODERATE;

export const selectDraftCustomServiceUrl = (state: RootState) =>
  state.customService?.draft?.search_settings?.server_side_search?.url ?? '';

export const selectDraftCustomServiceUser = (state: RootState) => {
  return (
    state.customService?.draft?.search_settings?.server_side_search?.username ??
    ''
  );
};
export const selectDraftCustomServicePass = (state: RootState) =>
  state.customService?.draft?.search_settings?.server_side_search?.password ??
  '';

export const selectDraftCustomServiceApiKey = (state: RootState) =>
  state.customService?.draft?.search_settings?.server_side_search?.apikey ?? '';

export const selectCustomServiceAuthType = (state: RootState) =>
  state.customService?.customServiceAuthType ?? '';

export const selectPreviouslySavedCustomServiceAuthType = (state: RootState) =>
  state.customService?.previouslySavedCustomServiceAuthType ?? '';

export const selectIsCreateCustomServiceLoading = (state: RootState) => {
  return state.customService?.createCustomServiceStatus === ApiStatus.LOADING;
};
export const selectCreateCustomServiceError = (state: RootState) => {
  return state.customService?.createCustomServiceError;
};

const selectIsSapConversationalSearchEnabled = (state: RootState) =>
  isFeatureEnabled(state, FeatureFlag.SAP_CONVERSATIONAL_SEARCH);

// Will be set to false if there are any validation errors when configuring the elastic setup.
export const selectIsCustomServiceSetupValid = (state: RootState) =>
  state.customService?.isCustomServiceSetupValid ?? false;

// Will be set to false if there are any validation errors when configuring the custom search setup or if any of the values
// are missing from the setup. Only the auth fields relevant to the current auth type are required for the setup to be
// valid. Password and api key are not required to be resubmitted when editing as long as their relevant auth type was
// the one previously saved for the skill.
export const selectIsServerSideSetupValid = (state: RootState) => {
  // Start by checking if there are any validation errors in the custom search setup.
  let validSetup = !!selectIsCustomServiceSetupValid(state);

  // Then, if the the url don't exist the setup is invalid.
  if (!selectDraftCustomServiceUrl(state)) {
    validSetup = false;
  }

  // Next check for different fields depending on the auth type.
  if (
    selectCustomServiceAuthType(state) ===
    CUSTOM_SERVICE_SEARCH_AUTH_TYPES.BASIC
  ) {
    // If the auth type is set to basic then a user is required.
    if (!selectDraftCustomServiceUser(state)) {
      validSetup = false;
    }

    // If the auth type is set to basic then a password is required.
    if (!selectDraftCustomServicePass(state)) {
      // However, if the user is editing an existing custom search setup, and the previously used CustomSearchAuthType is
      // also basic, then a password is not required.
      if (
        selectPreviouslySavedCustomServiceAuthType(state) !==
        CUSTOM_SERVICE_SEARCH_AUTH_TYPES.BASIC
      ) {
        // So if there's no password and the user is not editing, or they are editing but the
        // previouslySavedAuthType is not basic then the setup isn't valid.
        validSetup = false;
      }
    }
  } else if (
    selectCustomServiceAuthType(state) === CUSTOM_SERVICE_SEARCH_AUTH_TYPES.API
  ) {
    // If the auth type is set to api then an ApiKey is required.
    if (!selectDraftCustomServiceApiKey(state)) {
      // However, if the user is editing an existing setup, and the previously used AuthType is
      // also api, then an api key is not required.
      if (
        selectPreviouslySavedCustomServiceAuthType(state) !==
        CUSTOM_SERVICE_SEARCH_AUTH_TYPES.API
      ) {
        // So if there's no ApiKey and the user is not editing, or they are editing but the
        // previouslySavedAuthType is not api then the setup isn't valid.
        validSetup = false;
      }
    }
  }

  return validSetup;
};

// Will be set to false if there are any validation errors when configuring the elastic config.
export const selectIsElasticConfigValid = (state: RootState) =>
  state.customService?.isCustomServiceConfigValid ?? false;

export const selectCustomServiceSearchType = (state: RootState) =>
  state.customService?.selectedSearchType ?? SEARCH_PROVIDER_TYPES.SERVER_SIDE;

export const selectFilter = (state: RootState) => state.customService?.filter;

export const selectMetadata = (state: RootState) =>
  state.customService?.metadata;

// If Sap conversational search is enabled then there will not be a citations title value. However, if Sap
// conversational search is not enabled, and normal conversational search is enabled, then there needs to be a citations
// title value. Lastly, if normal conversational search is not enabled, then there does not need to be a citations title
// for the config to be valid.
const selectIsConversationalSearchConfigValid = (state: RootState) => {
  return (
    selectIsSapConversationalSearchEnabled(state) ||
    (selectDraftIsConversationalSearchEnabled(state)
      ? !!selectDraftCitationsTitleValue(state)
      : true)
  );
};

// Will be set to false if there are any validation errors when configuring the custom service config or if any of the values
// are missing from the config. There is no check for url, filter, or query body fields because those fields are
// optional. Lastly, check that the conversational search config is valid.
export const selectIsConfigValid = (state: RootState) => {
  return (
    !!selectIsElasticConfigValid(state) &&
    !!selectIsConversationalSearchConfigValid(state)
  );
};
